import { type Locale } from '../i18n'

export type SectorId =
  | 'mode'
  | 'cosmetique'
  | 'electronique'
  | 'jardin-exterieur'
  | 'maison-decoration'
  | 'parfumerie'

/**
 * Données neutres (non traduites) d'un secteur : identifiant, slug par langue et
 * paramètres d'animation numériques indépendants de la langue.
 */
export type SectorBase = {
  id: SectorId
  /** Slug validé par langue (cf. spec §3). */
  slug: Record<Locale, string>
  // Paramètres d'animation saisonnière (mois 0-indexés Jan→Déc) — non traduits.
  peakHighlightStart: number
  peakHighlightEnd: number
}

/**
 * Contenu textuel d'un secteur, par langue. = l'ancien type `Sector` MOINS `slug`
 * (déplacé dans `SectorBase`) et MOINS les champs numériques d'animation.
 */
export type SectorContent = {
  badge: string
  name: string
  /** Phrase grammatically correct after "pour", "de", "logistique" : "la mode", "l'électronique"… */
  inSector: string

  // SEO
  h1: string
  /** Short title for <title> + OG — kept to 50-60 chars to avoid SERP truncation */
  metaTitle: string
  taglineLead: string
  taglineItalic: string
  metaDescription: string
  heroIntro: string

  // Challenges → Responses (paired)
  challenges: { title: string; desc: string }[]
  responses: { title: string; desc: string }[]

  // Animation 1 captions
  flowItems: string[] // exactly 3
  flowBadge: string
  flowKicker: string

  // Animation 2 — seasonal
  peakLabel: string
  peakMonths: string
  peakMultiplier: string
  peakKpi1: { label: string; value: string }
  peakKpi2: { label: string; value: string }

  // Engagements chiffrés (replaces previous whyPoints)
  stats: { value: string; label: string; caption: string }[]

  // Promise statement (1 strong sentence)
  promise: string

  // SEO long-form FAQ
  faqs: { q: string; a: string }[]
}

/** Données neutres des 6 secteurs (slugs validés + params d'animation). */
export const SECTOR_BASE: SectorBase[] = [
  {
    id: 'mode',
    slug: { fr: 'mode', en: 'fashion', nl: 'mode' },
    peakHighlightStart: 8,
    peakHighlightEnd: 11,
  },
  {
    id: 'cosmetique',
    slug: { fr: 'cosmetique', en: 'cosmetics', nl: 'cosmetica' },
    peakHighlightStart: 10,
    peakHighlightEnd: 11,
  },
  {
    id: 'electronique',
    slug: { fr: 'electronique', en: 'electronics', nl: 'elektronica' },
    peakHighlightStart: 10,
    peakHighlightEnd: 11,
  },
  {
    id: 'jardin-exterieur',
    slug: { fr: 'jardin-exterieur', en: 'garden-outdoor', nl: 'buiten-tuin' },
    peakHighlightStart: 2,
    peakHighlightEnd: 5,
  },
  {
    id: 'maison-decoration',
    slug: { fr: 'maison-decoration', en: 'home-decor', nl: 'wonen-decoratie' },
    peakHighlightStart: 10,
    peakHighlightEnd: 11,
  },
  {
    id: 'parfumerie',
    slug: { fr: 'parfumerie', en: 'perfume', nl: 'parfumerie' },
    peakHighlightStart: 10,
    peakHighlightEnd: 11,
  },
]

/**
 * Contenu par langue, keyé par `SectorId`.
 * `fr` = contenu d'origine à l'identique ; `en`/`nl` traduits aux Tasks 3-4.
 */
export const SECTOR_CONTENT: Record<Locale, Record<SectorId, SectorContent>> = {
  fr: {
    mode: {
      badge: 'Mode',
      name: 'Mode & Textile',
      inSector: 'la mode',
      h1: 'Logistique e-commerce pour la mode et le textile',
      metaTitle: 'Logistique e-commerce mode & textile — Yaslan 3PL Belgique',
      taglineLead: 'La logistique qui suit',
      taglineItalic: 'le rythme de vos drops',
      metaDescription:
        "3PL spécialisé mode : drops absorbés, retours simplifiés, packaging à votre marque, pics saisonniers maîtrisés. Cut-off 15h, livraison 24h en Europe.",
      heroIntro:
        "De la capsule confidentielle au catalogue permanent — Yaslan absorbe vos drops, gère vos retours et expédie chaque pièce avec le soin d'un atelier. Pensé pour les marques de prêt-à-porter, sneakers, lingerie et accessoires textile.",
      challenges: [
        { title: 'Drops & lancements', desc: 'Pics de volume ×5 à ×10 sur 48 heures.' },
        { title: 'Retours élevés', desc: '25 à 35 % de taux moyen sur la mode en ligne.' },
        { title: 'Unboxing signature', desc: "L'expérience d'ouverture fait partie du produit." },
      ],
      responses: [
        { title: 'Capacité élastique', desc: 'Renforts opérationnels mobilisés 48 h avant chaque drop.' },
        { title: 'Portail retours marque blanche', desc: 'Remise en stock contrôlée sous 24 h.' },
        { title: 'Packaging à votre image', desc: "Cartons brandés, papier de soie, encartage promo, mots manuscrits." },
      ],
      flowItems: ['Réception drop', 'Pack signature', 'Expédition 24 h'],
      flowBadge: 'Flux Mode',
      flowKicker: 'De la collection au client',
      peakLabel: 'Pics mode',
      peakMonths: 'Septembre → Décembre + soldes',
      peakMultiplier: '×3,2',
      peakKpi1: { label: 'Expédié à temps', value: '99,4 %' },
      peakKpi2: { label: 'Capacité absorbée', value: '×3,2' },
      stats: [
        { value: '24 h', label: 'Remise en stock', caption: 'Vos retours redeviennent vendables sous un jour ouvré.' },
        { value: '×10', label: 'Drop absorbé', caption: "Renforts d'équipe planifiés 48 h avant chaque lancement." },
        { value: '100 %', label: 'Branding préservé', caption: 'Cartons, papier, étiquettes — tout reste à votre marque.' },
      ],
      promise:
        "Une logistique qui s'efface devant votre marque, pas une plateforme générique repeinte aux couleurs de la mode.",
      faqs: [
        {
          q: 'Yaslan peut-il absorber mes drops à capacité limitée ?',
          a: "Oui. Nous planifions des renforts opérationnels 48 à 72 h avant chaque drop pour absorber un volume jusqu'à 10× le quotidien sans dégrader le délai d'expédition.",
        },
        {
          q: 'Quel taux de retour observez-vous sur la mode et comment est-il géré ?',
          a: "Sur la mode en ligne, le taux de retour moyen oscille entre 25 et 35 %. Nos équipes traitent chaque retour en 4 étapes : réception, contrôle qualité (photos), classement (revendable / déclassé / casse), et remise en stock sous 24 h ouvrées sur les pièces revendables. Vous pilotez les règles depuis le dashboard.",
        },
        {
          q: "Pouvez-vous reprendre du stock déjà chez un autre 3PL ?",
          a: "Oui. Nous étudions chaque migration au cas par cas avec votre équipe et votre prestataire actuel : audit du catalogue, planning de transfert, synchronisation des systèmes, fenêtre minimale de coupure. Le délai dépend de la taille du stock, de la qualité des données existantes et du préavis contractuel — comptez en général entre 4 et 10 semaines pour une bascule maîtrisée, sans risquer de rupture côté clients.",
        },
        {
          q: 'Quels transporteurs proposez-vous pour les marques de mode ?',
          a: "bpost, GLS, DPD et Mondial Relay sont en standard. Les options express via DHL et UPS sont disponibles pour les commandes premium ou les zones longues.",
        },
        {
          q: 'Comment chiffrez-vous votre service pour la mode ?',
          a: "Tarif au m³ stocké + au colis préparé + tarif transporteur négocié. Aucun abonnement, aucun frais caché. Vous payez votre saison, pas vos m³ vides.",
        },
      ],
    },

    cosmetique: {
      badge: 'Cosmétique',
      name: 'Cosmétique & Beauté',
      inSector: 'la cosmétique',
      h1: 'Logistique e-commerce pour la cosmétique et la beauté',
      metaTitle: 'Logistique e-commerce cosmétique & beauté — Yaslan 3PL',
      taglineLead: 'Une logistique conforme,',
      taglineItalic: 'à la beauté du geste',
      metaDescription:
        "3PL spécialisé cosmétique : traçabilité par lot, conformité CLP, kits & coffrets, fragilité maîtrisée. Pour skincare, makeup et soins capillaires.",
      heroIntro:
        "Skincare, makeup, soins capillaires : la beauté exige une logistique méticuleuse. Suivi par lot, étiquettes CLP, kits cadeaux assemblés à la commande, contrôles fragilité — Yaslan applique la même rigueur que vos formulateurs.",
      challenges: [
        { title: 'Traçabilité par lot', desc: 'Chaque référence suivie par numéro de lot et date de péremption.' },
        { title: 'Conformité réglementaire', desc: 'Étiquettes CLP, mentions légales, fiches sécurité.' },
        { title: 'Kits & coffrets', desc: 'Assemblage à la commande, cadenciers fête des mères et Noël.' },
      ],
      responses: [
        { title: 'Suivi lot temps réel', desc: 'Inventaire par lot et alertes péremption automatiques.' },
        { title: "Contrôle d'étiquetage", desc: "Vérification CLP en réception, refus si non-conforme." },
        { title: 'Atelier kit dédié', desc: 'Coffrets multi-références préparés en îlots dédiés.' },
      ],
      flowItems: ['Contrôle lot', 'Assemblage kit', 'Expédition douce'],
      flowBadge: 'Flux Cosmétique',
      flowKicker: 'De la formulation au comptoir digital',
      peakLabel: 'Pics beauté',
      peakMonths: 'Mai (fête des mères) + Novembre-Décembre',
      peakMultiplier: '×2,8',
      peakKpi1: { label: 'Conformité étiquetage', value: '100 %' },
      peakKpi2: { label: 'Pic absorbé', value: '×2,8' },
      stats: [
        { value: 'FEFO', label: 'Suivi lot & péremption', caption: 'First Expired, First Out automatique sur chaque SKU.' },
        { value: '100 %', label: 'Conformité CLP', caption: 'Tout produit non-conforme isolé sous 4 h ouvrées.' },
        { value: '×2,8', label: 'Pic Noël absorbé', caption: 'Coffrets et kits assemblés en îlots dédiés.' },
      ],
      promise:
        "Une logistique aussi rigoureuse que votre laboratoire — pas un entrepôt générique qui range vos produits parmi mille autres.",
      faqs: [
        {
          q: 'Yaslan suit-il les numéros de lot et les dates de péremption ?',
          a: "Oui. Chaque réception est enregistrée par lot ; nous expédions en FEFO (First Expired, First Out) et alertons votre équipe avant chaque DDM critique.",
        },
        {
          q: 'Pouvez-vous assembler des coffrets multi-produits à la commande ?',
          a: "Oui, en îlots dédiés. Vous nous fournissez la nomenclature et le process d'assemblage, nous gérons l'opérationnel et la qualité.",
        },
        {
          q: "Comment garantissez-vous la conformité CLP de l'étiquetage ?",
          a: "Toute réception passe un contrôle visuel CLP. Tout produit non-conforme est isolé en quarantaine et nous vous alertons sous 4 h.",
        },
        {
          q: 'Êtes-vous équipés pour les produits sensibles à la chaleur ?',
          a: "Notre entrepôt est tempéré entre 11 et 18 °C en permanence. Pour le grand froid (sérums spécifiques, vaccins), nous orientons vers un partenaire spécialisé.",
        },
        {
          q: 'Comment gérez-vous les retours de produits cosmétiques ouverts ?',
          a: "Tri en réception : reconditionnable, déclassé ou destruction documentée. Vous décidez via le portail, photos à l'appui.",
        },
      ],
    },

    electronique: {
      badge: 'Électronique',
      name: 'Électronique & High-Tech',
      inSector: "l'électronique",
      h1: "Logistique e-commerce pour l'électronique et le high-tech",
      metaTitle: 'Logistique e-commerce électronique & high-tech — Yaslan 3PL',
      taglineLead: 'Une logistique sécurisée,',
      taglineItalic: 'numéro de série inclus',
      metaDescription:
        "3PL spécialisé électronique : sérialisation IMEI, gestion batteries lithium, RMA & garantie, anti-vol. Pour gadgets, audio, accessoires tech.",
      heroIntro:
        "Gadgets connectés, audio, accessoires tech : la valeur unitaire et la traçabilité numéro de série imposent un cadre strict. Yaslan sérialise, sécurise et gère vos retours sous garantie comme un service après-vente intégré.",
      challenges: [
        { title: 'Numéro de série', desc: 'IMEI, MAC, serial — chaque pièce traçable individuellement.' },
        { title: 'Batteries lithium', desc: 'Réglementation transport ADR / IATA section II.' },
        { title: 'Retours sous garantie', desc: 'Diagnostic, reconditionnement, réintégration ou casse.' },
      ],
      responses: [
        { title: "Scan SN à l'expédition", desc: 'Chaque numéro de série lié à une commande dans le dashboard.' },
        { title: 'Conformité ADR / IATA', desc: 'Documentation transport batteries, transporteurs agréés.' },
        { title: 'RMA structurée', desc: 'Diagnostic, photos, décision client en 48 h max.' },
      ],
      flowItems: ['Scan série', 'Anti-statique', 'Expédition tracée'],
      flowBadge: 'Flux Électronique',
      flowKicker: 'De la sérialisation à la livraison',
      peakLabel: 'Pics tech',
      peakMonths: 'Black Friday + Décembre',
      peakMultiplier: '×4,1',
      peakKpi1: { label: 'Numéros tracés', value: '100 %' },
      peakKpi2: { label: 'RMA traités <48 h', value: '97 %' },
      stats: [
        { value: '100 %', label: 'Numéros tracés', caption: 'IMEI, MAC, serial scannés et liés à la commande.' },
        { value: '<48 h', label: 'RMA traité', caption: 'Diagnostic, photos, décision client — sans délai.' },
        { value: '24/7', label: 'Vidéosurveillance', caption: 'Accès biométrique, scellés inviolables sur cartons.' },
      ],
      promise:
        "La rigueur d'un SAV intégré combinée à la souplesse d'un 3PL — votre marque garde le contrôle, nous portons l'opérationnel.",
      faqs: [
        {
          q: 'Comment Yaslan sécurise-t-il les produits électroniques de forte valeur en entrepôt ?',
          a: "Vidéosurveillance 24/7 avec accès biométrique aux zones high-tech, scellés inviolables sur tous les cartons sortants, double comptage en réception, et zones de stockage dédiées avec accès tracé pour les SKUs > 500 €. La traçabilité IMEI / MAC / serial relie chaque produit à sa commande dans votre dashboard.",
        },
        {
          q: 'Acceptez-vous les produits contenant des batteries lithium ?',
          a: "Oui, sous conditions ADR section II / IATA section II. Nous travaillons avec des transporteurs agréés et la documentation est intégrée à l'étiquette.",
        },
        {
          q: 'Comment gérez-vous les retours sous garantie ?',
          a: "Réception, diagnostic visuel et fonctionnel, photos, fiche RMA. Décision client (échange, remboursement, casse) sous 48 h ouvrées.",
        },
        {
          q: 'Acceptez-vous les produits avec piles alcalines ou non-lithium ?',
          a: "Oui, sans contrainte ADR particulière. La sérialisation et la traçabilité restent identiques aux produits lithium.",
        },
        {
          q: 'Pouvez-vous gérer le déballage et le reconditionnement ?',
          a: "Oui, en îlot dédié : test fonctionnel, mise à jour firmware si nécessaire, reconditionnement, étiquetage seconde vie.",
        },
      ],
    },

    'jardin-exterieur': {
      badge: 'Jardin',
      name: 'Jardin & Extérieur',
      inSector: 'le jardin & extérieur',
      h1: "Logistique e-commerce pour le jardin et l'outdoor",
      metaTitle: 'Logistique e-commerce jardin & outdoor — Yaslan 3PL',
      taglineLead: 'La logistique qui pousse',
      taglineItalic: 'au rythme des saisons',
      metaDescription:
        "3PL spécialisé jardin & outdoor : produits volumineux, ultra-saisonnalité, manutention spécifique. Pour outillage, mobilier, équipements outdoor.",
      heroIntro:
        "Mobilier de jardin, outillage, équipements outdoor, semences : volumes lourds, saisonnalité brutale et manutention dédiée. Yaslan dimensionne capacité et stockage selon votre cadencier saisonnier — sans payer pour des m³ vides l'hiver.",
      challenges: [
        { title: 'Volumes & poids', desc: 'Mobilier > 30 kg, outillage encombrant, palettisation.' },
        { title: 'Ultra-saisonnier', desc: '60 % du chiffre sur Mars-Juin pour beaucoup de marques.' },
        { title: 'Manutention spécifique', desc: 'Engins, transpalettes, gerbeurs requis.' },
      ],
      responses: [
        { title: 'Tarif au m³ flexible', desc: "Stockage dimensionné selon votre saisonnalité — pas d'abonnement." },
        { title: 'Capacité saisonnière', desc: 'Renforts mars-mai pour absorber le pic printemps.' },
        { title: 'Équipement adapté', desc: 'Transpalettes, gerbeurs, expertise manutention encombrant.' },
      ],
      flowItems: ['Réception palette', 'Manutention', 'Livraison J+1'],
      flowBadge: 'Flux Jardin',
      flowKicker: 'De la palette au jardin du client',
      peakLabel: 'Pics jardin',
      peakMonths: 'Mars → Juin',
      peakMultiplier: '×5,4',
      peakKpi1: { label: 'Tarif au m³', value: 'Flexible' },
      peakKpi2: { label: 'Pic absorbé', value: '×5,4' },
      stats: [
        { value: 'au m³', label: 'Tarif volume utilisé', caption: "Pas d'abonnement. Vous payez la haute saison, pas vos m³ vides." },
        { value: '×5,4', label: 'Pic printemps absorbé', caption: "Renforts d'équipe planifiés dès février." },
        { value: '1 200 kg', label: 'Palette expédiée', caption: 'Réseau transporteurs spécialisé colis volumineux.' },
      ],
      promise:
        "Un partenaire dimensionné pour vos pointes saisonnières, qui n'oublie pas que l'hiver vous ne vendez pas de tondeuses.",
      faqs: [
        {
          q: 'Pouvez-vous stocker du mobilier de jardin volumineux ?',
          a: "Oui — racks lourds, hauteur de stockage 6 m, gerbeurs et transpalettes électriques. Aucune limite raisonnable de volume ou de poids.",
        },
        {
          q: 'Comment gérez-vous le pic de printemps ?',
          a: "Renforts d'équipe planifiés dès février, capacité de stockage flexible et tarif au m³ — vous payez votre saison haute, pas vos m³ vides l'hiver.",
        },
        {
          q: 'Expédiez-vous les colis volumineux et palettes ?',
          a: "Oui, via notre réseau de transporteurs spécialisés (DPD Volume, DSV, transporteurs locaux palette). Livraison J+1 ou J+2 selon zone.",
        },
        {
          q: 'Stockez-vous des semences ou des plantes vivantes ?',
          a: "Les semences en sachet oui, sans contrainte. Les plantes vivantes nécessitent un partenaire horticole spécialisé : nous vous orientons.",
        },
        {
          q: 'Comment gérez-vous les retours de mobilier endommagé ?',
          a: "Tri en réception, photos, fiche de retour. Décision client (échange, remboursement, casse documentée) en moins de 72 h ouvrées.",
        },
      ],
    },

    'maison-decoration': {
      badge: 'Maison',
      name: 'Maison & Décoration',
      inSector: 'la maison & décoration',
      h1: 'Logistique e-commerce pour la maison et la décoration',
      metaTitle: 'Logistique e-commerce maison & déco — Yaslan 3PL Belgique',
      taglineLead: 'Une logistique soignée,',
      taglineItalic: 'comme votre intérieur',
      metaDescription:
        "3PL spécialisé maison & déco : fragilité maîtrisée, gros et petit colis, packaging cadeau, multi-colis. Pour bougies, luminaires, mobilier.",
      heroIntro:
        "Bougies, luminaires, art de la table, petit mobilier : la décoration mêle fragilité, encombrement variable et exigence d'unboxing. Yaslan emballe chaque pièce comme un cadeau et expédie en multi-colis quand il faut.",
      challenges: [
        { title: 'Fragilité élevée', desc: 'Verre, céramique, miroirs — taux de casse cible < 0,2 %.' },
        { title: 'Tailles très variables', desc: "D'une bougie à un canapé deux places dans le même catalogue." },
        { title: 'Cadeaux & unboxing', desc: "Emballage cadeau, mots, papier de soie, kraft signé." },
      ],
      responses: [
        { title: 'Calage anti-choc', desc: 'Mousse, papier bulle, double cartonnage sur articles fragiles.' },
        { title: 'Multi-colis intelligent', desc: 'Découpage automatique selon transporteur et destination.' },
        { title: 'Service emballage cadeau', desc: 'Activable par produit ou par client final.' },
      ],
      flowItems: ['Calage fragile', 'Emballage cadeau', 'Multi-colis'],
      flowBadge: 'Flux Maison',
      flowKicker: "De la pièce d'atelier au salon",
      peakLabel: 'Pics déco',
      peakMonths: 'Novembre → Décembre',
      peakMultiplier: '×2,4',
      peakKpi1: { label: 'Taux de casse', value: '< 0,2 %' },
      peakKpi2: { label: 'Pic absorbé', value: '×2,4' },
      stats: [
        { value: '<0,2 %', label: 'Taux de casse', caption: 'Procédures de calage spécifiques par catégorie fragile.' },
        { value: 'Multi', label: 'Découpage colis intelligent', caption: 'Routing automatique selon transporteur, taille et zone.' },
        { value: 'Cadeau', label: 'Emballage activable', caption: 'Papier kraft signé, ruban, mot personnalisé au choix.' },
      ],
      promise:
        "Chaque pièce traitée comme une œuvre d'atelier — du calage à l'étiquette, l'unboxing reste fidèle à votre image.",
      faqs: [
        {
          q: 'Quel est votre taux de casse sur les articles fragiles ?',
          a: "Inférieur à 0,2 % grâce à des procédures de calage spécifiques par catégorie et un double cartonnage systématique pour le verre et la céramique.",
        },
        {
          q: 'Pouvez-vous proposer un emballage cadeau au client final ?',
          a: "Oui, activable au niveau produit ou commande. Papier kraft signé, ruban, mot personnalisé sont disponibles en option.",
        },
        {
          q: 'Comment expédiez-vous les commandes multi-articles très différentes ?',
          a: "Notre routing automatique calcule le découpage optimal en multi-colis selon transporteur, dimensions et zone — pour minimiser le coût d'expédition.",
        },
        {
          q: 'Pouvez-vous expédier des meubles à monter (kit) ?',
          a: "Oui, fréquent en déco. Multi-colis avec liaison de tracking pour que votre client reçoive l'ensemble en cohérence.",
        },
        {
          q: 'Acceptez-vous les retours de pièces fragiles déjà ouvertes ?',
          a: "Oui. Inspection visuelle, photos, classement (revendable, déclassé, casse). Décision sous 48 h.",
        },
      ],
    },

    parfumerie: {
      badge: 'Parfumerie',
      name: 'Parfumerie',
      inSector: 'la parfumerie',
      h1: 'Logistique e-commerce pour la parfumerie',
      metaTitle: 'Logistique e-commerce parfumerie — Yaslan 3PL Belgique',
      taglineLead: 'Une logistique réglementée,',
      taglineItalic: 'au flacon près',
      metaDescription:
        "3PL spécialisé parfumerie : transport conforme alcool, verre fragile, coffrets, traçabilité lot. Pour eaux de parfum, eaux de toilette, bougies parfumées.",
      heroIntro:
        "Eaux de parfum, eaux de toilette, brumes, bougies parfumées : alcool, verre, et exigence cadeau. Yaslan transporte vos parfums en conformité ADR, emballe chaque flacon comme un trésor et assemble vos coffrets sur commande.",
      challenges: [
        { title: 'Transport alcool', desc: 'Conformité ADR, transporteurs agréés matières dangereuses.' },
        { title: 'Verre fragile', desc: 'Flacons sertis, atomiseurs, vaporisateurs.' },
        { title: 'Coffrets & écrins', desc: 'Assemblage, ruban, ouatine, étuis luxe.' },
      ],
      responses: [
        { title: 'Filière ADR active', desc: 'Documentation transport et étiquetage matière dangereuse inclus.' },
        { title: 'Calage flacon dédié', desc: 'Mousse moulée, double cartonnage, scellés inviolables.' },
        { title: 'Atelier coffret', desc: 'Coffrets assemblés à la commande avec ruban et écrin.' },
      ],
      flowItems: ['Conformité ADR', 'Calage flacon', 'Coffret signé'],
      flowBadge: 'Flux Parfumerie',
      flowKicker: 'Du flacon au geste rituel',
      peakLabel: 'Pics parfumerie',
      peakMonths: 'Juin (fête des pères) + Novembre-Décembre',
      peakMultiplier: '×3,6',
      peakKpi1: { label: 'Conformité ADR', value: '100 %' },
      peakKpi2: { label: 'Pic Noël absorbé', value: '×3,6' },
      stats: [
        { value: 'ADR', label: 'Filière transport active', caption: 'Documentation matières dangereuses incluse, transporteurs agréés.' },
        { value: '<0,15 %', label: 'Casse flacon', caption: 'Mousse moulée, double cartonnage, scellés inviolables.' },
        { value: '×3,6', label: 'Pic Noël absorbé', caption: 'Coffrets et écrins assemblés en atelier dédié.' },
      ],
      promise:
        "Une logistique digne de la rareté de vos jus — réglementaire au flacon près, soignée jusqu'au ruban du coffret.",
      faqs: [
        {
          q: 'Yaslan peut-il transporter des parfums (matière dangereuse alcool) ?',
          a: "Oui — nous opérons une filière ADR active. Documentation transport, étiquetage matière dangereuse et transporteurs agréés sont inclus.",
        },
        {
          q: 'Comment limitez-vous la casse sur les flacons en verre ?',
          a: "Calage en mousse moulée par référence, double cartonnage systématique, scellés inviolables. Taux de casse inférieur à 0,15 % constaté.",
        },
        {
          q: 'Pouvez-vous assembler des coffrets cadeaux ?',
          a: "Oui, en atelier dédié pendant les pics. Ruban, écrin, ouatine, mot personnalisé : tout est paramétrable par référence.",
        },
        {
          q: 'Quels sont les transporteurs agréés pour le transport de parfums ?',
          a: "Nous travaillons avec des transporteurs ADR-agréés : DPD, GLS, transporteurs locaux spécialisés. La documentation matière dangereuse est intégrée à l'étiquette d'expédition.",
        },
        {
          q: "Pouvez-vous expédier des parfums en dehors de l'Union Européenne ?",
          a: "Oui, mais avec déclaration douanière spécifique alcool. Délais et coûts variables selon destination — nous chiffrons au cas par cas.",
        },
      ],
    },
  },
  en: {
    mode: {
      badge: 'Fashion',
      name: 'Fashion & Textiles',
      inSector: 'fashion',
      h1: 'E-commerce logistics for fashion and textiles',
      metaTitle: 'E-commerce logistics for fashion & textiles — Yaslan 3PL',
      taglineLead: 'Logistics that keep pace',
      taglineItalic: 'with your drops',
      metaDescription:
        'Fashion-focused 3PL: drops absorbed, returns simplified, branded packaging, seasonal peaks under control. 3pm cut-off, next-day delivery across Europe.',
      heroIntro:
        'From the confidential capsule to the permanent catalogue — Yaslan absorbs your drops, handles your returns and ships every piece with the care of an atelier. Built for ready-to-wear, sneaker, lingerie and textile accessory brands.',
      challenges: [
        { title: 'Drops & launches', desc: 'Volume peaks of ×5 to ×10 over 48 hours.' },
        { title: 'High returns', desc: '25 to 35% average return rate in online fashion.' },
        { title: 'Signature unboxing', desc: 'The opening experience is part of the product.' },
      ],
      responses: [
        { title: 'Elastic capacity', desc: 'Operational reinforcements mobilised 48h before each drop.' },
        { title: 'White-label returns portal', desc: 'Controlled restocking within 24h.' },
        { title: 'Packaging in your image', desc: 'Branded boxes, tissue paper, promo inserts, handwritten notes.' },
      ],
      flowItems: ['Drop received', 'Signature pack', 'Next-day shipping'],
      flowBadge: 'Fashion flow',
      flowKicker: 'From collection to customer',
      peakLabel: 'Fashion peaks',
      peakMonths: 'September → December + sales',
      peakMultiplier: '×3.2',
      peakKpi1: { label: 'Shipped on time', value: '99.4%' },
      peakKpi2: { label: 'Capacity absorbed', value: '×3.2' },
      stats: [
        { value: '24h', label: 'Restocking', caption: 'Your returns become sellable again within one working day.' },
        { value: '×10', label: 'Drop absorbed', caption: 'Team reinforcements planned 48h before each launch.' },
        { value: '100%', label: 'Branding preserved', caption: 'Boxes, paper, labels — everything stays on brand.' },
      ],
      promise:
        'Logistics that step aside for your brand, not a generic platform repainted in fashion colours.',
      faqs: [
        {
          q: 'Can Yaslan absorb my limited-capacity drops?',
          a: 'Yes. We plan operational reinforcements 48 to 72h before each drop to absorb volumes of up to 10× the daily average without degrading shipping times.',
        },
        {
          q: 'What return rate do you see in fashion and how is it managed?',
          a: 'In online fashion, the average return rate ranges between 25 and 35%. Our teams process each return in 4 steps: receipt, quality control (photos), sorting (sellable / downgraded / damaged), and restocking within 24 working hours for sellable items. You control the rules from the dashboard.',
        },
        {
          q: 'Can you take over stock already held by another 3PL?',
          a: 'Yes. We assess each migration case by case with your team and your current provider: catalogue audit, transfer schedule, system synchronisation, minimal cut-over window. The lead time depends on the size of the stock, the quality of the existing data and the contractual notice period — typically allow between 4 and 10 weeks for a controlled switch-over, with no risk of stock-out for your customers.',
        },
        {
          q: 'Which carriers do you offer for fashion brands?',
          a: 'bpost, GLS, DPD and Mondial Relay are standard. Express options via DHL and UPS are available for premium orders or long-distance zones.',
        },
        {
          q: 'How do you price your service for fashion?',
          a: 'A rate per m³ stored + per parcel picked + a negotiated carrier rate. No subscription, no hidden fees. You pay for your season, not for empty m³.',
        },
      ],
    },

    cosmetique: {
      badge: 'Cosmetics',
      name: 'Cosmetics & Beauty',
      inSector: 'cosmetics',
      h1: 'E-commerce logistics for cosmetics and beauty',
      metaTitle: 'E-commerce logistics for cosmetics & beauty — Yaslan 3PL',
      taglineLead: 'Compliant logistics,',
      taglineItalic: 'as graceful as the gesture',
      metaDescription:
        'Cosmetics-focused 3PL: batch traceability, CLP compliance, kits & gift sets, fragility handled with care. For skincare, makeup and hair care.',
      heroIntro:
        'Skincare, makeup, hair care: beauty demands meticulous logistics. Batch tracking, CLP labels, gift sets assembled to order, fragility checks — Yaslan applies the same rigour as your formulators.',
      challenges: [
        { title: 'Batch traceability', desc: 'Every reference tracked by batch number and expiry date.' },
        { title: 'Regulatory compliance', desc: 'CLP labels, legal notices, safety data sheets.' },
        { title: 'Kits & gift sets', desc: "Assembled to order, schedules for Mother's Day and Christmas." },
      ],
      responses: [
        { title: 'Real-time batch tracking', desc: 'Batch-level inventory and automatic expiry alerts.' },
        { title: 'Labelling control', desc: 'CLP verification on receipt, rejection if non-compliant.' },
        { title: 'Dedicated kit workshop', desc: 'Multi-reference gift sets prepared in dedicated cells.' },
      ],
      flowItems: ['Batch control', 'Kit assembly', 'Gentle shipping'],
      flowBadge: 'Cosmetics flow',
      flowKicker: 'From formulation to digital counter',
      peakLabel: 'Beauty peaks',
      peakMonths: "May (Mother's Day) + November-December",
      peakMultiplier: '×2.8',
      peakKpi1: { label: 'Labelling compliance', value: '100%' },
      peakKpi2: { label: 'Peak absorbed', value: '×2.8' },
      stats: [
        { value: 'FEFO', label: 'Batch & expiry tracking', caption: 'Automatic First Expired, First Out on every SKU.' },
        { value: '100%', label: 'CLP compliance', caption: 'Any non-compliant product isolated within 4 working hours.' },
        { value: '×2.8', label: 'Christmas peak absorbed', caption: 'Gift sets and kits assembled in dedicated cells.' },
      ],
      promise:
        'Logistics as rigorous as your laboratory — not a generic warehouse that files your products among a thousand others.',
      faqs: [
        {
          q: 'Does Yaslan track batch numbers and expiry dates?',
          a: 'Yes. Every receipt is recorded by batch; we ship on a FEFO basis (First Expired, First Out) and alert your team before each critical best-before date.',
        },
        {
          q: 'Can you assemble multi-product gift sets to order?',
          a: 'Yes, in dedicated cells. You provide the bill of materials and the assembly process, we handle the operations and quality.',
        },
        {
          q: 'How do you guarantee CLP compliance of labelling?',
          a: 'Every receipt undergoes a visual CLP check. Any non-compliant product is isolated in quarantine and we alert you within 4h.',
        },
        {
          q: 'Are you equipped for heat-sensitive products?',
          a: 'Our warehouse is kept at a constant 11 to 18°C. For deep cold (specific serums, vaccines), we refer you to a specialist partner.',
        },
        {
          q: 'How do you handle returns of opened cosmetic products?',
          a: 'Sorting on receipt: reconditionable, downgraded or documented destruction. You decide via the portal, with photos to support the decision.',
        },
      ],
    },

    electronique: {
      badge: 'Electronics',
      name: 'Electronics & High-Tech',
      inSector: 'electronics',
      h1: 'E-commerce logistics for electronics and high-tech',
      metaTitle: 'E-commerce logistics for electronics & high-tech — Yaslan 3PL',
      taglineLead: 'Secure logistics,',
      taglineItalic: 'serial number included',
      metaDescription:
        'Electronics-focused 3PL: IMEI serialisation, lithium battery handling, RMA & warranty, anti-theft. For gadgets, audio, tech accessories.',
      heroIntro:
        'Connected gadgets, audio, tech accessories: unit value and serial-number traceability call for a strict framework. Yaslan serialises, secures and manages your warranty returns like an integrated after-sales service.',
      challenges: [
        { title: 'Serial number', desc: 'IMEI, MAC, serial — every item individually traceable.' },
        { title: 'Lithium batteries', desc: 'ADR / IATA section II transport regulations.' },
        { title: 'Warranty returns', desc: 'Diagnosis, reconditioning, reintegration or write-off.' },
      ],
      responses: [
        { title: 'SN scan at dispatch', desc: 'Every serial number linked to an order in the dashboard.' },
        { title: 'ADR / IATA compliance', desc: 'Battery transport documentation, approved carriers.' },
        { title: 'Structured RMA', desc: 'Diagnosis, photos, customer decision within 48h max.' },
      ],
      flowItems: ['Serial scan', 'Anti-static', 'Tracked shipping'],
      flowBadge: 'Electronics flow',
      flowKicker: 'From serialisation to delivery',
      peakLabel: 'Tech peaks',
      peakMonths: 'Black Friday + December',
      peakMultiplier: '×4.1',
      peakKpi1: { label: 'Numbers tracked', value: '100%' },
      peakKpi2: { label: 'RMA handled <48h', value: '97%' },
      stats: [
        { value: '100%', label: 'Numbers tracked', caption: 'IMEI, MAC, serial scanned and linked to the order.' },
        { value: '<48h', label: 'RMA handled', caption: 'Diagnosis, photos, customer decision — without delay.' },
        { value: '24/7', label: 'Video surveillance', caption: 'Biometric access, tamper-proof seals on boxes.' },
      ],
      promise:
        'The rigour of an integrated after-sales service combined with the flexibility of a 3PL — your brand keeps control, we carry the operations.',
      faqs: [
        {
          q: 'How does Yaslan secure high-value electronics in the warehouse?',
          a: 'Round-the-clock video surveillance with biometric access to high-tech zones, tamper-proof seals on every outbound box, double counting on receipt, and dedicated storage zones with tracked access for SKUs over €500. IMEI / MAC / serial traceability links every product to its order in your dashboard.',
        },
        {
          q: 'Do you accept products containing lithium batteries?',
          a: 'Yes, under ADR section II / IATA section II conditions. We work with approved carriers and the documentation is integrated into the label.',
        },
        {
          q: 'How do you handle warranty returns?',
          a: 'Receipt, visual and functional diagnosis, photos, RMA sheet. Customer decision (exchange, refund, write-off) within 48 working hours.',
        },
        {
          q: 'Do you accept products with alkaline or non-lithium batteries?',
          a: 'Yes, with no specific ADR constraints. Serialisation and traceability remain identical to lithium products.',
        },
        {
          q: 'Can you handle unpacking and reconditioning?',
          a: 'Yes, in a dedicated cell: functional testing, firmware update if needed, reconditioning, second-life labelling.',
        },
      ],
    },

    'jardin-exterieur': {
      badge: 'Garden',
      name: 'Garden & Outdoor',
      inSector: 'garden & outdoor',
      h1: 'E-commerce logistics for garden and outdoor',
      metaTitle: 'E-commerce logistics for garden & outdoor — Yaslan 3PL',
      taglineLead: 'Logistics that grow',
      taglineItalic: 'with the seasons',
      metaDescription:
        'Garden & outdoor 3PL: bulky products, extreme seasonality, specific handling. For tools, furniture, outdoor equipment.',
      heroIntro:
        "Garden furniture, tools, outdoor equipment, seeds: heavy volumes, brutal seasonality and dedicated handling. Yaslan sizes capacity and storage to your seasonal schedule — without paying for empty m³ in winter.",
      challenges: [
        { title: 'Volume & weight', desc: 'Furniture > 30 kg, bulky tools, palletisation.' },
        { title: 'Ultra-seasonal', desc: '60% of turnover in March-June for many brands.' },
        { title: 'Specific handling', desc: 'Machinery, pallet trucks, stackers required.' },
      ],
      responses: [
        { title: 'Flexible rate per m³', desc: 'Storage sized to your seasonality — no subscription.' },
        { title: 'Seasonal capacity', desc: 'March-May reinforcements to absorb the spring peak.' },
        { title: 'Suitable equipment', desc: 'Pallet trucks, stackers, bulky-handling expertise.' },
      ],
      flowItems: ['Pallet received', 'Handling', 'Next-day delivery'],
      flowBadge: 'Garden flow',
      flowKicker: "From pallet to the customer's garden",
      peakLabel: 'Garden peaks',
      peakMonths: 'March → June',
      peakMultiplier: '×5.4',
      peakKpi1: { label: 'Rate per m³', value: 'Flexible' },
      peakKpi2: { label: 'Peak absorbed', value: '×5.4' },
      stats: [
        { value: 'per m³', label: 'Rate on volume used', caption: 'No subscription. You pay for the high season, not for empty m³.' },
        { value: '×5.4', label: 'Spring peak absorbed', caption: 'Team reinforcements planned from February.' },
        { value: '1,200 kg', label: 'Pallet shipped', caption: 'Carrier network specialised in bulky parcels.' },
      ],
      promise:
        "A partner sized for your seasonal peaks, who doesn't forget that you don't sell lawnmowers in winter.",
      faqs: [
        {
          q: 'Can you store bulky garden furniture?',
          a: 'Yes — heavy-duty racking, 6 m storage height, stackers and electric pallet trucks. No reasonable limit on volume or weight.',
        },
        {
          q: 'How do you manage the spring peak?',
          a: "Team reinforcements planned from February, flexible storage capacity and a rate per m³ — you pay for your high season, not for empty m³ in winter.",
        },
        {
          q: 'Do you ship bulky parcels and pallets?',
          a: 'Yes, via our network of specialist carriers (DPD Volume, DSV, local pallet carriers). Next-day or two-day delivery depending on the zone.',
        },
        {
          q: 'Do you store seeds or live plants?',
          a: 'Packeted seeds yes, with no constraints. Live plants require a specialist horticultural partner: we can refer you.',
        },
        {
          q: 'How do you handle returns of damaged furniture?',
          a: 'Sorting on receipt, photos, return sheet. Customer decision (exchange, refund, documented write-off) within 72 working hours.',
        },
      ],
    },

    'maison-decoration': {
      badge: 'Home',
      name: 'Home & Decor',
      inSector: 'home & decor',
      h1: 'E-commerce logistics for home and decor',
      metaTitle: 'E-commerce logistics for home & decor — Yaslan 3PL',
      taglineLead: 'Careful logistics,',
      taglineItalic: 'like your interior',
      metaDescription:
        'Home & decor 3PL: fragility handled with care, large and small parcels, gift packaging, multi-parcel. For candles, lighting, furniture.',
      heroIntro:
        'Candles, lighting, tableware, small furniture: decor combines fragility, variable bulk and the demand for great unboxing. Yaslan packs every piece like a gift and ships multi-parcel when needed.',
      challenges: [
        { title: 'High fragility', desc: 'Glass, ceramic, mirrors — target breakage rate < 0.2%.' },
        { title: 'Highly variable sizes', desc: 'From a candle to a two-seater sofa in the same catalogue.' },
        { title: 'Gifts & unboxing', desc: 'Gift wrapping, notes, tissue paper, signed kraft.' },
      ],
      responses: [
        { title: 'Shock-proof cushioning', desc: 'Foam, bubble wrap, double boxing on fragile items.' },
        { title: 'Smart multi-parcel', desc: 'Automatic splitting by carrier and destination.' },
        { title: 'Gift wrapping service', desc: 'Activatable per product or per end customer.' },
      ],
      flowItems: ['Fragile cushioning', 'Gift wrapping', 'Multi-parcel'],
      flowBadge: 'Home flow',
      flowKicker: 'From workshop piece to living room',
      peakLabel: 'Decor peaks',
      peakMonths: 'November → December',
      peakMultiplier: '×2.4',
      peakKpi1: { label: 'Breakage rate', value: '< 0.2%' },
      peakKpi2: { label: 'Peak absorbed', value: '×2.4' },
      stats: [
        { value: '<0.2%', label: 'Breakage rate', caption: 'Specific cushioning procedures for each fragile category.' },
        { value: 'Multi', label: 'Smart parcel splitting', caption: 'Automatic routing by carrier, size and zone.' },
        { value: 'Gift', label: 'Activatable wrapping', caption: 'Signed kraft paper, ribbon, personalised note as you choose.' },
      ],
      promise:
        'Every piece treated like a workshop creation — from cushioning to label, the unboxing stays true to your image.',
      faqs: [
        {
          q: 'What is your breakage rate on fragile items?',
          a: 'Below 0.2%, thanks to category-specific cushioning procedures and systematic double boxing for glass and ceramic.',
        },
        {
          q: 'Can you offer gift wrapping to the end customer?',
          a: 'Yes, activatable at product or order level. Signed kraft paper, ribbon and personalised note are available as options.',
        },
        {
          q: 'How do you ship multi-item orders with very different products?',
          a: 'Our automatic routing calculates the optimal multi-parcel split by carrier, dimensions and zone — to minimise shipping cost.',
        },
        {
          q: 'Can you ship flat-pack furniture (kits)?',
          a: 'Yes, common in decor. Multi-parcel with linked tracking so your customer receives the whole set consistently.',
        },
        {
          q: 'Do you accept returns of fragile pieces already opened?',
          a: 'Yes. Visual inspection, photos, sorting (sellable, downgraded, damaged). Decision within 48h.',
        },
      ],
    },

    parfumerie: {
      badge: 'Perfume',
      name: 'Perfume',
      inSector: 'perfume',
      h1: 'E-commerce logistics for perfume',
      metaTitle: 'E-commerce logistics for perfume — Yaslan 3PL Belgium',
      taglineLead: 'Regulated logistics,',
      taglineItalic: 'down to the bottle',
      metaDescription:
        'Perfume-focused 3PL: alcohol-compliant transport, fragile glass, gift sets, batch traceability. For eau de parfum, eau de toilette, scented candles.',
      heroIntro:
        'Eau de parfum, eau de toilette, mists, scented candles: alcohol, glass, and the demands of gifting. Yaslan transports your fragrances in ADR compliance, packs every bottle like a treasure and assembles your gift sets to order.',
      challenges: [
        { title: 'Alcohol transport', desc: 'ADR compliance, carriers approved for dangerous goods.' },
        { title: 'Fragile glass', desc: 'Crimped bottles, atomisers, sprays.' },
        { title: 'Gift sets & cases', desc: 'Assembly, ribbon, wadding, luxury sleeves.' },
      ],
      responses: [
        { title: 'Active ADR channel', desc: 'Transport documentation and dangerous-goods labelling included.' },
        { title: 'Dedicated bottle cushioning', desc: 'Moulded foam, double boxing, tamper-proof seals.' },
        { title: 'Gift set workshop', desc: 'Gift sets assembled to order with ribbon and case.' },
      ],
      flowItems: ['ADR compliance', 'Bottle cushioning', 'Signed gift set'],
      flowBadge: 'Perfume flow',
      flowKicker: 'From bottle to ritual gesture',
      peakLabel: 'Perfume peaks',
      peakMonths: "June (Father's Day) + November-December",
      peakMultiplier: '×3.6',
      peakKpi1: { label: 'ADR compliance', value: '100%' },
      peakKpi2: { label: 'Christmas peak absorbed', value: '×3.6' },
      stats: [
        { value: 'ADR', label: 'Active transport channel', caption: 'Dangerous-goods documentation included, approved carriers.' },
        { value: '<0.15%', label: 'Bottle breakage', caption: 'Moulded foam, double boxing, tamper-proof seals.' },
        { value: '×3.6', label: 'Christmas peak absorbed', caption: 'Gift sets and cases assembled in a dedicated workshop.' },
      ],
      promise:
        'Logistics worthy of the rarity of your juices — regulated down to the bottle, refined right down to the gift-set ribbon.',
      faqs: [
        {
          q: 'Can Yaslan transport perfumes (alcohol as a dangerous good)?',
          a: 'Yes — we operate an active ADR channel. Transport documentation, dangerous-goods labelling and approved carriers are all included.',
        },
        {
          q: 'How do you limit breakage on glass bottles?',
          a: 'Cushioning in moulded foam per reference, systematic double boxing, tamper-proof seals. Breakage rate recorded below 0.15%.',
        },
        {
          q: 'Can you assemble gift sets?',
          a: 'Yes, in a dedicated workshop during peaks. Ribbon, case, wadding, personalised note: everything is configurable per reference.',
        },
        {
          q: 'Which carriers are approved for transporting perfumes?',
          a: 'We work with ADR-approved carriers: DPD, GLS, specialist local carriers. The dangerous-goods documentation is integrated into the shipping label.',
        },
        {
          q: 'Can you ship perfumes outside the European Union?',
          a: 'Yes, but with a specific customs declaration for alcohol. Lead times and costs vary by destination — we quote case by case.',
        },
      ],
    },
  } satisfies Record<SectorId, SectorContent>,
  nl: {
    mode: {
      badge: 'Mode',
      name: 'Mode & Textiel',
      inSector: 'mode',
      h1: 'E-commerce logistiek voor mode en textiel',
      metaTitle: 'E-commerce logistiek mode & textiel — Yaslan 3PL',
      taglineLead: 'Logistiek die het tempo',
      taglineItalic: 'van je drops volgt',
      metaDescription:
        'Gespecialiseerde 3PL voor mode: drops opgevangen, retourzendingen vereenvoudigd, verpakking met jouw merk, seizoenspieken onder controle. Cut-off 15u, levering binnen 24u in Europa.',
      heroIntro:
        'Van de exclusieve capsule tot de vaste catalogus — Yaslan vangt je drops op, beheert je retourzendingen en verzendt elk stuk met de zorg van een atelier. Ontworpen voor merken in prêt-à-porter, sneakers, lingerie en textielaccessoires.',
      challenges: [
        { title: 'Drops & lanceringen', desc: 'Volumepieken van ×5 tot ×10 binnen 48 uur.' },
        { title: 'Hoge retourgraad', desc: 'Gemiddeld 25 tot 35 % retouren in online mode.' },
        { title: 'Kenmerkende unboxing', desc: 'De openingservaring is onderdeel van het product.' },
      ],
      responses: [
        { title: 'Elastische capaciteit', desc: 'Operationele versterking 48u voor elke drop ingezet.' },
        { title: 'Retourportaal in white-label', desc: 'Gecontroleerde herinname op voorraad binnen 24u.' },
        { title: 'Verpakking met jouw uitstraling', desc: 'Gemerkte dozen, zijdepapier, promo-inserts, handgeschreven kaartjes.' },
      ],
      flowItems: ['Drop ontvangen', 'Kenmerkende pack', 'Verzending binnen 24u'],
      flowBadge: 'Modeflow',
      flowKicker: 'Van collectie tot klant',
      peakLabel: 'Modepieken',
      peakMonths: 'September → December + uitverkoop',
      peakMultiplier: '×3,2',
      peakKpi1: { label: 'Op tijd verzonden', value: '99,4 %' },
      peakKpi2: { label: 'Opgevangen capaciteit', value: '×3,2' },
      stats: [
        { value: '24u', label: 'Herinname op voorraad', caption: 'Je retourzendingen worden binnen één werkdag weer verkoopbaar.' },
        { value: '×10', label: 'Drop opgevangen', caption: 'Teamversterking gepland 48u voor elke lancering.' },
        { value: '100 %', label: 'Branding behouden', caption: 'Dozen, papier, etiketten — alles blijft van jouw merk.' },
      ],
      promise:
        'Logistiek die zich wegcijfert voor jouw merk, geen generiek platform overgeschilderd in modekleuren.',
      faqs: [
        {
          q: 'Kan Yaslan mijn drops met beperkte capaciteit opvangen?',
          a: 'Ja. We plannen operationele versterking 48 tot 72u voor elke drop om een volume tot 10× het dagelijkse gemiddelde op te vangen zonder de verzendtijd te verslechteren.',
        },
        {
          q: 'Welke retourgraad zien jullie in de mode en hoe wordt die beheerd?',
          a: 'In online mode schommelt de gemiddelde retourgraad tussen 25 en 35 %. Onze teams verwerken elke retour in 4 stappen: ontvangst, kwaliteitscontrole (foto’s), sortering (verkoopbaar / afgewaardeerd / breuk) en herinname op voorraad binnen 24 werkuren voor verkoopbare stukken. Jij stuurt de regels aan vanuit het dashboard.',
        },
        {
          q: 'Kunnen jullie voorraad overnemen die al bij een andere 3PL ligt?',
          a: 'Ja. We bekijken elke migratie geval per geval met jouw team en je huidige dienstverlener: audit van de catalogus, overdrachtsplanning, synchronisatie van de systemen, minimaal onderbrekingsvenster. De doorlooptijd hangt af van de omvang van de voorraad, de kwaliteit van de bestaande gegevens en de contractuele opzegtermijn — reken doorgaans op 4 tot 10 weken voor een beheerste overstap, zonder risico op nee-verkoop bij je klanten.',
        },
        {
          q: 'Welke vervoerders bieden jullie voor modemerken?',
          a: 'bpost, GLS, DPD en Mondial Relay zijn standaard. Expressopties via DHL en UPS zijn beschikbaar voor premium bestellingen of verre zones.',
        },
        {
          q: 'Hoe bepalen jullie de prijs van je dienst voor de mode?',
          a: 'Tarief per opgeslagen m³ + per verwerkte bestelling + onderhandeld vervoerderstarief. Geen abonnement, geen verborgen kosten. Je betaalt voor je seizoen, niet voor lege m³.',
        },
      ],
    },

    cosmetique: {
      badge: 'Cosmetica',
      name: 'Cosmetica & Beauty',
      inSector: 'cosmetica',
      h1: 'E-commerce logistiek voor cosmetica en beauty',
      metaTitle: 'E-commerce logistiek cosmetica & beauty — Yaslan 3PL',
      taglineLead: 'Logistiek die voldoet,',
      taglineItalic: 'zo mooi als het gebaar',
      metaDescription:
        'Gespecialiseerde 3PL voor cosmetica: traceerbaarheid per batch, CLP-naleving, kits & geschenksets, breekbaarheid onder controle. Voor skincare, make-up en haarverzorging.',
      heroIntro:
        'Skincare, make-up, haarverzorging: beauty vereist zorgvuldige logistiek. Tracering per batch, CLP-etiketten, geschenksets op bestelling samengesteld, breekbaarheidscontroles — Yaslan past dezelfde nauwkeurigheid toe als jouw formulatoren.',
      challenges: [
        { title: 'Traceerbaarheid per batch', desc: 'Elke referentie gevolgd via batchnummer en houdbaarheidsdatum.' },
        { title: 'Wettelijke naleving', desc: 'CLP-etiketten, wettelijke vermeldingen, veiligheidsfiches.' },
        { title: 'Kits & geschenksets', desc: 'Samenstelling op bestelling, planning voor moederdag en Kerst.' },
      ],
      responses: [
        { title: 'Realtime batchtracering', desc: 'Voorraad per batch en automatische houdbaarheidswaarschuwingen.' },
        { title: 'Controle van etikettering', desc: 'CLP-controle bij ontvangst, weigering bij niet-conformiteit.' },
        { title: 'Toegewijd kit-atelier', desc: 'Geschenksets met meerdere referenties samengesteld in toegewijde cellen.' },
      ],
      flowItems: ['Batchcontrole', 'Kit samenstellen', 'Zachte verzending'],
      flowBadge: 'Cosmeticaflow',
      flowKicker: 'Van formulering tot digitale toonbank',
      peakLabel: 'Beautypieken',
      peakMonths: 'Mei (moederdag) + november-december',
      peakMultiplier: '×2,8',
      peakKpi1: { label: 'Naleving etikettering', value: '100 %' },
      peakKpi2: { label: 'Piek opgevangen', value: '×2,8' },
      stats: [
        { value: 'FEFO', label: 'Batch- & houdbaarheidstracering', caption: 'Automatische First Expired, First Out op elke SKU.' },
        { value: '100 %', label: 'CLP-naleving', caption: 'Elk niet-conform product binnen 4 werkuren geïsoleerd.' },
        { value: '×2,8', label: 'Kerstpiek opgevangen', caption: 'Geschenksets en kits samengesteld in toegewijde cellen.' },
      ],
      promise:
        'Logistiek even nauwkeurig als jouw laboratorium — geen generiek magazijn dat je producten tussen duizend andere opbergt.',
      faqs: [
        {
          q: 'Volgt Yaslan batchnummers en houdbaarheidsdata?',
          a: 'Ja. Elke ontvangst wordt per batch geregistreerd; we verzenden volgens FEFO (First Expired, First Out) en waarschuwen je team voor elke kritieke houdbaarheidsdatum.',
        },
        {
          q: 'Kunnen jullie geschenksets met meerdere producten op bestelling samenstellen?',
          a: 'Ja, in toegewijde cellen. Jij levert de stuklijst en het samenstellingsproces aan, wij verzorgen de uitvoering en de kwaliteit.',
        },
        {
          q: 'Hoe garanderen jullie de CLP-naleving van de etikettering?',
          a: 'Elke ontvangst ondergaat een visuele CLP-controle. Elk niet-conform product wordt in quarantaine geïsoleerd en we waarschuwen je binnen 4u.',
        },
        {
          q: 'Zijn jullie uitgerust voor warmtegevoelige producten?',
          a: 'Ons magazijn wordt permanent op 11 tot 18 °C gehouden. Voor extreme koude (specifieke serums, vaccins) verwijzen we je door naar een gespecialiseerde partner.',
        },
        {
          q: 'Hoe behandelen jullie retouren van geopende cosmeticaproducten?',
          a: 'Sortering bij ontvangst: herverpakbaar, afgewaardeerd of gedocumenteerde vernietiging. Jij beslist via het portaal, met foto’s ter onderbouwing.',
        },
      ],
    },

    electronique: {
      badge: 'Elektronica',
      name: 'Elektronica & High-Tech',
      inSector: 'elektronica',
      h1: 'E-commerce logistiek voor elektronica en high-tech',
      metaTitle: 'E-commerce logistiek elektronica & high-tech — Yaslan 3PL',
      taglineLead: 'Beveiligde logistiek,',
      taglineItalic: 'serienummer inbegrepen',
      metaDescription:
        'Gespecialiseerde 3PL voor elektronica: IMEI-serialisatie, beheer van lithiumbatterijen, RMA & garantie, antidiefstal. Voor gadgets, audio, techaccessoires.',
      heroIntro:
        'Connected gadgets, audio, techaccessoires: de waarde per stuk en de tracering via serienummer vragen om een strikt kader. Yaslan serialiseert, beveiligt en beheert je garantieretouren als een geïntegreerde klantenservice.',
      challenges: [
        { title: 'Serienummer', desc: 'IMEI, MAC, serie — elk stuk individueel traceerbaar.' },
        { title: 'Lithiumbatterijen', desc: 'Transportregelgeving ADR / IATA sectie II.' },
        { title: 'Garantieretouren', desc: 'Diagnose, herconditionering, herintegratie of afschrijving.' },
      ],
      responses: [
        { title: 'SN-scan bij verzending', desc: 'Elk serienummer gekoppeld aan een bestelling in het dashboard.' },
        { title: 'ADR / IATA-naleving', desc: 'Transportdocumentatie batterijen, erkende vervoerders.' },
        { title: 'Gestructureerde RMA', desc: 'Diagnose, foto’s, klantbeslissing binnen max. 48u.' },
      ],
      flowItems: ['Serie scannen', 'Antistatisch', 'Getraceerde verzending'],
      flowBadge: 'Elektronicaflow',
      flowKicker: 'Van serialisatie tot levering',
      peakLabel: 'Techpieken',
      peakMonths: 'Black Friday + december',
      peakMultiplier: '×4,1',
      peakKpi1: { label: 'Getraceerde nummers', value: '100 %' },
      peakKpi2: { label: 'RMA verwerkt <48u', value: '97 %' },
      stats: [
        { value: '100 %', label: 'Getraceerde nummers', caption: 'IMEI, MAC, serie gescand en gekoppeld aan de bestelling.' },
        { value: '<48u', label: 'RMA verwerkt', caption: 'Diagnose, foto’s, klantbeslissing — zonder vertraging.' },
        { value: '24/7', label: 'Videobewaking', caption: 'Biometrische toegang, onschendbare zegels op dozen.' },
      ],
      promise:
        'De nauwkeurigheid van een geïntegreerde klantenservice gecombineerd met de soepelheid van een 3PL — jouw merk houdt de controle, wij dragen de uitvoering.',
      faqs: [
        {
          q: 'Hoe beveiligt Yaslan elektronica met hoge waarde in het magazijn?',
          a: 'Videobewaking 24/7 met biometrische toegang tot de high-techzones, onschendbare zegels op alle uitgaande dozen, dubbele telling bij ontvangst en toegewijde opslagzones met getraceerde toegang voor SKU’s boven € 500. De tracering via IMEI / MAC / serie koppelt elk product aan zijn bestelling in je dashboard.',
        },
        {
          q: 'Aanvaarden jullie producten met lithiumbatterijen?',
          a: 'Ja, onder de voorwaarden ADR sectie II / IATA sectie II. We werken met erkende vervoerders en de documentatie is in het etiket geïntegreerd.',
        },
        {
          q: 'Hoe behandelen jullie garantieretouren?',
          a: 'Ontvangst, visuele en functionele diagnose, foto’s, RMA-fiche. Klantbeslissing (omruil, terugbetaling, afschrijving) binnen 48 werkuren.',
        },
        {
          q: 'Aanvaarden jullie producten met alkaline- of niet-lithiumbatterijen?',
          a: 'Ja, zonder specifieke ADR-beperking. De serialisatie en tracering blijven identiek aan lithiumproducten.',
        },
        {
          q: 'Kunnen jullie het uitpakken en herconditioneren verzorgen?',
          a: 'Ja, in een toegewijde cel: functionele test, firmware-update indien nodig, herconditionering, etikettering voor tweede leven.',
        },
      ],
    },

    'jardin-exterieur': {
      badge: 'Tuin',
      name: 'Tuin & Buiten',
      inSector: 'tuin & buiten',
      h1: 'E-commerce logistiek voor tuin en buiten',
      metaTitle: 'E-commerce logistiek tuin & buiten — Yaslan 3PL',
      taglineLead: 'Logistiek die groeit',
      taglineItalic: 'op het ritme van de seizoenen',
      metaDescription:
        'Gespecialiseerde 3PL voor tuin & buiten: volumineuze producten, extreme seizoensgebondenheid, specifieke handling. Voor gereedschap, meubilair, buitenuitrusting.',
      heroIntro:
        'Tuinmeubilair, gereedschap, buitenuitrusting, zaden: zware volumes, bruuske seizoensgebondenheid en toegewijde handling. Yaslan stemt capaciteit en opslag af op je seizoensplanning — zonder te betalen voor lege m³ in de winter.',
      challenges: [
        { title: 'Volumes & gewicht', desc: 'Meubilair > 30 kg, omvangrijk gereedschap, palletisatie.' },
        { title: 'Sterk seizoensgebonden', desc: 'Voor veel merken 60 % van de omzet in maart-juni.' },
        { title: 'Specifieke handling', desc: 'Machines, palletwagens, stapelaars vereist.' },
      ],
      responses: [
        { title: 'Flexibel tarief per m³', desc: 'Opslag afgestemd op je seizoensgebondenheid — zonder abonnement.' },
        { title: 'Seizoenscapaciteit', desc: 'Versterking maart-mei om de lentepiek op te vangen.' },
        { title: 'Aangepaste uitrusting', desc: 'Palletwagens, stapelaars, expertise in omvangrijke handling.' },
      ],
      flowItems: ['Pallet ontvangen', 'Handling', 'Levering volgende dag'],
      flowBadge: 'Tuinflow',
      flowKicker: 'Van pallet tot de tuin van de klant',
      peakLabel: 'Tuinpieken',
      peakMonths: 'Maart → Juni',
      peakMultiplier: '×5,4',
      peakKpi1: { label: 'Tarief per m³', value: 'Flexibel' },
      peakKpi2: { label: 'Piek opgevangen', value: '×5,4' },
      stats: [
        { value: 'per m³', label: 'Tarief op gebruikt volume', caption: 'Zonder abonnement. Je betaalt het hoogseizoen, niet je lege m³.' },
        { value: '×5,4', label: 'Lentepiek opgevangen', caption: 'Teamversterking vanaf februari gepland.' },
        { value: '1.200 kg', label: 'Pallet verzonden', caption: 'Vervoerdersnetwerk gespecialiseerd in volumineuze pakketten.' },
      ],
      promise:
        'Een partner op maat van je seizoenspieken, die niet vergeet dat je in de winter geen grasmaaiers verkoopt.',
      faqs: [
        {
          q: 'Kunnen jullie volumineus tuinmeubilair opslaan?',
          a: 'Ja — zware stellingen, opslaghoogte van 6 m, stapelaars en elektrische palletwagens. Geen redelijke limiet op volume of gewicht.',
        },
        {
          q: 'Hoe beheren jullie de lentepiek?',
          a: 'Teamversterking vanaf februari gepland, flexibele opslagcapaciteit en een tarief per m³ — je betaalt je hoogseizoen, niet je lege m³ in de winter.',
        },
        {
          q: 'Verzenden jullie volumineuze pakketten en pallets?',
          a: 'Ja, via ons netwerk van gespecialiseerde vervoerders (DPD Volume, DSV, lokale palletvervoerders). Levering volgende dag of dag+2 afhankelijk van de zone.',
        },
        {
          q: 'Slaan jullie zaden of levende planten op?',
          a: 'Zaden in zakjes wel, zonder beperking. Levende planten vereisen een gespecialiseerde tuinbouwpartner: we verwijzen je door.',
        },
        {
          q: 'Hoe behandelen jullie retouren van beschadigd meubilair?',
          a: 'Sortering bij ontvangst, foto’s, retourfiche. Klantbeslissing (omruil, terugbetaling, gedocumenteerde afschrijving) binnen 72 werkuren.',
        },
      ],
    },

    'maison-decoration': {
      badge: 'Wonen',
      name: 'Wonen & Decoratie',
      inSector: 'wonen & decoratie',
      h1: 'E-commerce logistiek voor wonen en decoratie',
      metaTitle: 'E-commerce logistiek wonen & decoratie — Yaslan 3PL',
      taglineLead: 'Verzorgde logistiek,',
      taglineItalic: 'net als jouw interieur',
      metaDescription:
        'Gespecialiseerde 3PL voor wonen & decoratie: breekbaarheid onder controle, grote en kleine pakketten, geschenkverpakking, multipakket. Voor kaarsen, verlichting, meubilair.',
      heroIntro:
        'Kaarsen, verlichting, tafelgerei, klein meubilair: decoratie combineert breekbaarheid, wisselend volume en een hoge unboxing-eis. Yaslan verpakt elk stuk als een geschenk en verzendt in multipakket waar nodig.',
      challenges: [
        { title: 'Hoge breekbaarheid', desc: 'Glas, keramiek, spiegels — streefcijfer breukgraad < 0,2 %.' },
        { title: 'Sterk wisselende maten', desc: 'Van een kaars tot een tweezitsbank in dezelfde catalogus.' },
        { title: 'Geschenken & unboxing', desc: 'Geschenkverpakking, kaartjes, zijdepapier, gemerkt kraftpapier.' },
      ],
      responses: [
        { title: 'Schokbestendige opvulling', desc: 'Schuim, bubbeltjesfolie, dubbele verdozing op breekbare artikelen.' },
        { title: 'Slim multipakket', desc: 'Automatische opsplitsing per vervoerder en bestemming.' },
        { title: 'Geschenkverpakkingsservice', desc: 'Activeerbaar per product of per eindklant.' },
      ],
      flowItems: ['Breekbaar opvullen', 'Geschenkverpakking', 'Multipakket'],
      flowBadge: 'Wonenflow',
      flowKicker: 'Van atelierstuk tot woonkamer',
      peakLabel: 'Decopieken',
      peakMonths: 'November → December',
      peakMultiplier: '×2,4',
      peakKpi1: { label: 'Breukgraad', value: '< 0,2 %' },
      peakKpi2: { label: 'Piek opgevangen', value: '×2,4' },
      stats: [
        { value: '<0,2 %', label: 'Breukgraad', caption: 'Specifieke opvulprocedures per breekbare categorie.' },
        { value: 'Multi', label: 'Slimme pakketopsplitsing', caption: 'Automatische routing per vervoerder, maat en zone.' },
        { value: 'Geschenk', label: 'Activeerbare verpakking', caption: 'Gemerkt kraftpapier, lint, gepersonaliseerd kaartje naar keuze.' },
      ],
      promise:
        'Elk stuk behandeld als een atelierwerk — van opvulling tot etiket blijft de unboxing trouw aan jouw uitstraling.',
      faqs: [
        {
          q: 'Wat is jullie breukgraad op breekbare artikelen?',
          a: 'Minder dan 0,2 %, dankzij categoriespecifieke opvulprocedures en systematische dubbele verdozing voor glas en keramiek.',
        },
        {
          q: 'Kunnen jullie geschenkverpakking aan de eindklant aanbieden?',
          a: 'Ja, activeerbaar op product- of bestellingsniveau. Gemerkt kraftpapier, lint en gepersonaliseerd kaartje zijn beschikbaar als optie.',
        },
        {
          q: 'Hoe verzenden jullie bestellingen met meerdere, zeer verschillende artikelen?',
          a: 'Onze automatische routing berekent de optimale opsplitsing in multipakket per vervoerder, afmetingen en zone — om de verzendkost te minimaliseren.',
        },
        {
          q: 'Kunnen jullie zelfbouwmeubilair (bouwpakket) verzenden?',
          a: 'Ja, vaak voorkomend in decoratie. Multipakket met gekoppelde tracking zodat je klant het geheel samenhangend ontvangt.',
        },
        {
          q: 'Aanvaarden jullie retouren van reeds geopende breekbare stukken?',
          a: 'Ja. Visuele inspectie, foto’s, sortering (verkoopbaar, afgewaardeerd, breuk). Beslissing binnen 48u.',
        },
      ],
    },

    parfumerie: {
      badge: 'Parfumerie',
      name: 'Parfumerie',
      inSector: 'parfumerie',
      h1: 'E-commerce logistiek voor parfumerie',
      metaTitle: 'E-commerce logistiek parfumerie — Yaslan 3PL',
      taglineLead: 'Gereguleerde logistiek,',
      taglineItalic: 'tot op de flacon',
      metaDescription:
        'Gespecialiseerde 3PL voor parfumerie: alcoholconform transport, breekbaar glas, geschenksets, batchtracering. Voor eau de parfum, eau de toilette, geurkaarsen.',
      heroIntro:
        'Eau de parfum, eau de toilette, geurnevels, geurkaarsen: alcohol, glas en de geschenkeis. Yaslan vervoert je parfums conform ADR, verpakt elke flacon als een schat en stelt je geschenksets op bestelling samen.',
      challenges: [
        { title: 'Alcoholtransport', desc: 'ADR-naleving, vervoerders erkend voor gevaarlijke goederen.' },
        { title: 'Breekbaar glas', desc: 'Gefelste flacons, atomizers, verstuivers.' },
        { title: 'Geschenksets & etuis', desc: 'Samenstelling, lint, wattering, luxehoezen.' },
      ],
      responses: [
        { title: 'Actief ADR-kanaal', desc: 'Transportdocumentatie en etikettering gevaarlijke goederen inbegrepen.' },
        { title: 'Toegewijde flaconopvulling', desc: 'Gevormd schuim, dubbele verdozing, onschendbare zegels.' },
        { title: 'Geschenkset-atelier', desc: 'Geschenksets op bestelling samengesteld met lint en etui.' },
      ],
      flowItems: ['ADR-naleving', 'Flacon opvullen', 'Gemerkte geschenkset'],
      flowBadge: 'Parfumerieflow',
      flowKicker: 'Van flacon tot ritueel gebaar',
      peakLabel: 'Parfumeriepieken',
      peakMonths: 'Juni (vaderdag) + november-december',
      peakMultiplier: '×3,6',
      peakKpi1: { label: 'ADR-naleving', value: '100 %' },
      peakKpi2: { label: 'Kerstpiek opgevangen', value: '×3,6' },
      stats: [
        { value: 'ADR', label: 'Actief transportkanaal', caption: 'Documentatie gevaarlijke goederen inbegrepen, erkende vervoerders.' },
        { value: '<0,15 %', label: 'Flaconbreuk', caption: 'Gevormd schuim, dubbele verdozing, onschendbare zegels.' },
        { value: '×3,6', label: 'Kerstpiek opgevangen', caption: 'Geschenksets en etuis samengesteld in een toegewijd atelier.' },
      ],
      promise:
        'Logistiek de zeldzaamheid van je jus waardig — gereguleerd tot op de flacon, verzorgd tot het lint van de geschenkset.',
      faqs: [
        {
          q: 'Kan Yaslan parfums (alcohol als gevaarlijke goederen) vervoeren?',
          a: 'Ja — we beheren een actief ADR-kanaal. Transportdocumentatie, etikettering gevaarlijke goederen en erkende vervoerders zijn inbegrepen.',
        },
        {
          q: 'Hoe beperken jullie de breuk op glazen flacons?',
          a: 'Opvulling in gevormd schuim per referentie, systematische dubbele verdozing, onschendbare zegels. Vastgestelde breukgraad onder 0,15 %.',
        },
        {
          q: 'Kunnen jullie geschenksets samenstellen?',
          a: 'Ja, in een toegewijd atelier tijdens de pieken. Lint, etui, wattering, gepersonaliseerd kaartje: alles is instelbaar per referentie.',
        },
        {
          q: 'Welke vervoerders zijn erkend voor het vervoer van parfums?',
          a: 'We werken met ADR-erkende vervoerders: DPD, GLS, gespecialiseerde lokale vervoerders. De documentatie gevaarlijke goederen is in het verzendetiket geïntegreerd.',
        },
        {
          q: 'Kunnen jullie parfums buiten de Europese Unie verzenden?',
          a: 'Ja, maar met een specifieke douaneaangifte voor alcohol. Doorlooptijden en kosten variëren per bestemming — we maken een offerte geval per geval.',
        },
      ],
    },
  } satisfies Record<SectorId, SectorContent>,
}

/** Liste de tous les ids de secteurs (ordre du registre). */
export const ALL_SECTOR_IDS: SectorId[] = SECTOR_BASE.map((b) => b.id)

/** Résout un secteur par (langue, slug localisé). Contenu en fallback FR. */
export function getSectorByLocaleSlug(
  locale: Locale,
  slug: string,
): { base: SectorBase; content: SectorContent } | undefined {
  const base = SECTOR_BASE.find((b) => b.slug[locale] === slug)
  if (!base) return undefined
  const content = SECTOR_CONTENT[locale][base.id] ?? SECTOR_CONTENT.fr[base.id] // FR fallback
  return { base, content }
}

/** Données neutres d'un secteur par id. */
export function getSectorById(id: SectorId): SectorBase | undefined {
  return SECTOR_BASE.find((b) => b.id === id)
}
